import { Table, TableBody, TableHead, TableRow } from "@mui/material";
import * as React from "react";
import { ICell } from "../../types";
import { Colors } from "../util/Colors";
import { TextDot } from "./Dots";
import { HistoryTableHeaderLabel } from "./HistoryTableHeaderLabel";
import { getHistoryTableHeaderCellStyle } from "./historyTableHeaderStyles";
import { StyledTableCell } from "./StyledTableCell";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableLabel } from "./TableLabel";

type IHeaderItem = {
    name: string;
    label?: string | React.ReactNode;
    topLabel?: string;
    date?: string;
    patientName?: string;
    style?: React.CSSProperties;
};

type IProps = {
    headerItems?: IHeaderItem[];
    rows: ICell[][];
    style?: React.CSSProperties;
    stickyHeader?: boolean;
};

export const CurrentValueTable = ({ headerItems, rows, style, stickyHeader }: IProps) => (
    <Table
        style={{
            tableLayout: "fixed",
            ...style,
            ...(stickyHeader ? { borderCollapse: "separate", borderSpacing: 0 } : undefined),
        }}
    >
        <colgroup>
            <col style={{ width: "26.8908%" }} />
            <col style={{ width: "22.6891%" }} />
            <col style={{ width: "50.4202%" }} />
        </colgroup>
        <TableHead>
            <TableRow>
                {headerItems?.map((headerItem, index) => (
                    <TableHeaderCell
                        key={index}
                        style={
                            stickyHeader
                                ? getHistoryTableHeaderCellStyle(stickyHeader, {
                                      textAlign: index === 0 ? "left" : "center",
                                  })
                                : {
                                      textAlign: index === 0 ? "left" : "center",
                                      whiteSpace: "nowrap",
                                      verticalAlign: "middle",
                                      height: "48px",
                                  }
                        }
                    >
                        {stickyHeader ? (
                            <HistoryTableHeaderLabel
                                topLabel={
                                    headerItem.topLabel ??
                                    (typeof headerItem.label === "string" ? headerItem.label : undefined)
                                }
                                date={headerItem.date}
                                patientName={headerItem.patientName}
                                align={index === 0 ? "left" : "center"}
                            />
                        ) : (
                            headerItem.label
                        )}
                    </TableHeaderCell>
                ))}
            </TableRow>
        </TableHead>
        <TableBody>
            {rows.map((row, index) => (
                <TableRow key={index} style={{ minHeight: "48px" }}>
                    {row.map((cell, cellIndex) => (
                        <StyledTableCell
                            key={cellIndex}
                            style={{
                                color: cell.color,
                                borderRight: cellIndex < row.length - 1 ? `1px solid ${Colors.GREY_200}` : undefined,
                                backgroundColor: "unset",
                                verticalAlign: "middle",
                                textAlign: "center",
                                height: "58px",
                                padding: "8px 4px",
                            }}
                        >
                            <div
                                style={
                                    cellIndex !== 0
                                        ? {
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              height: "100%",
                                              minHeight: "32px",
                                          }
                                        : {
                                              display: "flex",
                                              alignItems: "center",
                                              height: "100%",
                                              minHeight: "32px",
                                          }
                                }
                            >
                                {cell.color && <TextDot style={{ backgroundColor: cell.color, marginRight: 6 }} />}
                                {cellIndex === 0 && typeof cell.content === "string" ? (
                                    <TableLabel
                                        style={{
                                            width: 160,
                                            textAlign: "left",
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: Colors.TEXT_LIGHT,
                                        }}
                                        tooltip={cell.content}
                                        maxLines={2}
                                    >
                                        {cell.content}
                                    </TableLabel>
                                ) : cellIndex !== 0 &&
                                  (cell.content === undefined ||
                                      cell.content === "" ||
                                      typeof cell.content === "string" ||
                                      typeof cell.content === "number") ? (
                                    <TableLabel
                                        style={{
                                            maxWidth: "100%",
                                            minWidth: 0,
                                            fontWeight: "inherit",
                                            color: "inherit",
                                        }}
                                        maxLines={2}
                                    >
                                        {`${cell.content !== undefined && cell.content !== "" ? cell.content : "-"}${
                                            cell.content !== undefined && cell.content !== "" && cell.adornmentText
                                                ? `\u00A0${cell.adornmentText}`
                                                : ""
                                        }`}
                                    </TableLabel>
                                ) : (
                                    <div style={{ whiteSpace: "nowrap" }}>
                                        {cell.content !== undefined && cell.content !== "" ? cell.content : "-"}
                                        {cell.content !== undefined && cell.content !== "" && cell.adornmentText && (
                                            <span>&nbsp;{cell.adornmentText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </StyledTableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    </Table>
);
